import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchOpenTrades, fetchWallets } from "../../store/slices/tradeSlice";
import { setSelectedWallet } from "../../store/slices/uiSlice";
import { usePrices } from "../../hooks/usePrices";
import { usePriceFlash } from "../../hooks/usePriceFlash";
import LivePrice from "../../components/ui/LivePrice";
import { tradeService } from "../../services/trades";
import TradingViewChart from "../../components/features/charts/TradingViewChart";
import OrderBook from "../../components/features/trades/OrderBook";
import PairSelector from "../../components/features/trades/PairSelector";
import Pagination from "../../components/ui/Pagination";
import { Modal } from "../../components/ui/Modal";
import { formatUSD, formatPnL, pnlClass } from "../../utils/formatters";
import {
  COINS,
  LEVERAGE_OPTIONS,
  DURATION_OPTIONS,
} from "../../utils/constants";
import type { Trade as TradeType } from "../../types";
import toast from "react-hot-toast";
import { Ico } from "../../components/ui/Icon";

// ── Schema ────────────────────────────────────────────────────────────────────
const schema = z.object({
  cryptocurrency: z.string(),
  amount: z
    .string()
    .refine((v) => !isNaN(Number(v)) && Number(v) > 0, "Enter a valid amount"),
  wallet_type: z.string() as any,
  leverage: z.string(),
  duration_minutes: z.string(),
  auto_compound: z.boolean().optional(),
  stop_loss: z.string().optional(),
  take_profit: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

const COIN_COLORS: Record<string, string> = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  XRP: "#00AAE4",
  SOL: "#9945FF",
  BNB: "#F3BA2F",
  ADA: "#0033AD",
  DOGE: "#C2A633",
  DOT: "#E6007A",
};

const CloseReason: React.FC<{ reason?: string | null }> = ({ reason }) => {
  const map: Record<string, { t: string; c: string }> = {
    stop_loss: { t: "Stop Loss", c: "text-red-500" },
    take_profit: { t: "Take Profit", c: "text-primary" },
    duration: { t: "Timer", c: "text-gray-400" },
    force: { t: "Force", c: "text-orange-400" },
    manual: { t: "Manual", c: "text-gray-500" },
  };
  const m = reason ? map[reason] : null;
  return m ? (
    <span className={`text-[10px] font-semibold ${m.c}`}>{m.t}</span>
  ) : (
    <span className="text-gray-700 text-[10px]">—</span>
  );
};

// ── Coin strip ────────────────────────────────────────────────────────────────
const CoinStrip: React.FC<{
  selectedCoin: string;
  prices: Record<string, number>;
  marketData: Record<string, any>;
  onSelect: (s: string) => void;
}> = ({ selectedCoin, prices, marketData, onSelect }) => {
  const flash = usePriceFlash(prices);
  return (
    <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
      {(COINS as any[]).map((coin: any) => {
        const price = prices[coin.symbol] || 0;
        const change = marketData[coin.symbol]?.change_24h || 0;
        const isUp = change >= 0;
        const active = selectedCoin === coin.symbol;
        const f = flash[coin.symbol];
        return (
          <button
            key={coin.symbol}
            onClick={() => onSelect(coin.symbol)}
            className={[
              "flex flex-col items-start px-3 py-2 rounded-xl border transition-all shrink-0 min-w-[76px]",
              active
                ? "bg-gray-900 border-gray-700"
                : "bg-white border-gray-700 hover:border-gray-600",
              f === "up" ? "!border-primary/50" : "",
              f === "down" ? "!border-red-500/50" : "",
            ].join(" ")}
          >
            <div className="flex items-center gap-1 mb-0.5">
              <span
                className="w-3.5 h-3.5 rounded-full text-[7px] font-black flex items-center justify-center shrink-0"
                style={{
                  background: `${COIN_COLORS[coin.symbol]}22`,
                  color: COIN_COLORS[coin.symbol],
                }}
              >
                {coin.symbol[0]}
              </span>
              <span
                className={`text-[10px] font-mono font-bold ${active ? "text-gray-50" : "text-gray-500"}`}
              >
                {coin.symbol}
              </span>
            </div>
            <span
              className={`text-[11px] font-mono font-bold transition-colors duration-100 ${
                f === "up"
                  ? "text-primary"
                  : f === "down"
                    ? "text-red-500"
                    : active
                      ? "text-gray-50"
                      : "text-gray-400"
              }`}
            >
              {price > 0
                ? price < 1
                  ? `$${price.toFixed(4)}`
                  : `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : "…"}
            </span>
            <span
              className={`text-[9px] font-mono ${isUp ? "text-primary/80" : "text-red-500/80"}`}
            >
              {isUp ? "+" : ""}
              {change.toFixed(2)}%
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ── Open positions table ──────────────────────────────────────────────────────
const OpenPositionsTable: React.FC<{
  trades: TradeType[];
  prices: Record<string, number>;
  onClose: (t: TradeType) => void;
  onSelect: (t: TradeType) => void;
  closingId: number | null;
}> = ({ trades, prices, onClose, onSelect, closingId }) => {
  if (trades.length === 0)
    return (
      <div className="flex flex-col items-center justify-center py-14">
        <div className="w-12 h-12 rounded-2xl bg-gray-900 border border-gray-800/60 flex items-center justify-center mb-3">
          <Ico name="chart" className="w-5 h-5 text-gray-700" />
        </div>
        <p className="text-gray-600 text-sm">No open positions</p>
        <p className="text-gray-700 text-xs mt-0.5">
          Place your first trade above
        </p>
      </div>
    );

  const totalPnL = trades.reduce((s, t) => s + (t.unrealised_pnl ?? 0), 0);
  const totalSize = trades.reduce(
    (s, t) => s + parseFloat(String(t.amount)),
    0,
  );

  return (
    <div className="overflow-x-auto">
      {/* min-w keeps table readable; users can scroll horizontally on small screens */}
      <div className="min-w-[600px]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_72px] gap-1.5 px-3 py-2 border-b border-gray-800/50">
          {["Symbol", "Size", "Entry", "Mark", "PnL", "Time", "Acct", ""].map(
            (h) => (
              <span
                key={h}
                className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
              >
                {h}
              </span>
            ),
          )}
        </div>
        <div className="divide-y divide-gray-800/30">
          {trades.map((trade) => {
            const price = prices[trade.cryptocurrency] || 0;
            const isBuy = trade.direction === "buy";
            const entry = parseFloat(String(trade.entry_price));
            const pnlPct =
              entry > 0 && price > 0
                ? ((price - entry) / entry) *
                  100 *
                  (isBuy ? 1 : -1) *
                  trade.leverage
                : 0;
            const pnl =
              entry > 0 && price > 0
                ? ((isBuy ? price - entry : entry - price) / entry) *
                  parseFloat(String(trade.amount)) *
                  trade.leverage
                : (trade.unrealised_pnl ?? 0);
            return (
              <div
                key={trade.id}
                onClick={() => onSelect(trade)}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_72px] gap-1.5 px-3 py-2.5 items-center hover:bg-gray-900 transition-colors group cursor-pointer"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-[8px] font-black shrink-0"
                    style={{
                      background: `${COIN_COLORS[trade.cryptocurrency]}15`,
                      color: COIN_COLORS[trade.cryptocurrency],
                    }}
                  >
                    {trade.cryptocurrency[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-50 text-[11px] font-mono font-bold truncate">
                      {trade.cryptocurrency}/USDT
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span
                        className={`text-[8px] font-bold px-1 py-0.5 rounded ${isBuy ? "bg-primary/15 text-primary" : "bg-red-500/15 text-red-500"}`}
                      >
                        {isBuy ? "▲ L" : "▼ S"}
                      </span>
                      <span className="text-gray-600 text-[8px] font-mono">
                        {trade.leverage}x
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-50 text-[11px] font-mono font-semibold">
                  {formatUSD(trade.amount)}
                </p>
                <p className="text-gray-300 text-[11px] font-mono">
                  $
                  {entry.toLocaleString(undefined, {
                    maximumFractionDigits: entry < 1 ? 4 : 2,
                  })}
                </p>
                <LivePrice
                  price={price}
                  className="text-[11px] font-semibold"
                  baseColor="text-gray-300"
                  showArrow
                />
                <div>
                  <p
                    className={`text-[11px] font-mono font-bold ${pnlClass(pnl)}`}
                  >
                    {formatPnL(pnl)}
                  </p>
                  <p className={`text-[9px] font-mono ${pnlClass(pnlPct)}`}>
                    {pnlPct >= 0 ? "+" : ""}
                    {pnlPct.toFixed(2)}%
                  </p>
                </div>
                <span className="text-[10px] font-mono text-gray-400">
                  {(trade.opened_at || trade.created_at)?.slice(11, 16) || "—"}
                </span>
                <span
                  className={`text-[9px] font-bold ${trade.wallet_type === "real" ? "text-primary" : "text-blue-400"}`}
                >
                  {trade.wallet_type?.toUpperCase()}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(trade);
                  }}
                  disabled={closingId === trade.id}
                  className="px-2 py-1 rounded-lg text-[9px] font-bold font-display bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500/20 disabled:opacity-40 transition-all opacity-0 group-hover:opacity-100"
                >
                  {closingId === trade.id ? "…" : "Close"}
                </button>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_72px] gap-1.5 px-3 py-2 border-t border-gray-800/50 bg-gray-900/20">
          <span className="text-[10px] text-gray-600">
            {trades.length} position{trades.length !== 1 ? "s" : ""}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">
            {formatUSD(totalSize)}
          </span>
          <span />
          <span />
          <span
            className={`text-[10px] font-mono font-bold ${pnlClass(totalPnL)}`}
          >
            {formatPnL(totalPnL)}
          </span>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
};

// ── Closed trades ─────────────────────────────────────────────────────────────
const ClosedTradesTable: React.FC<{
  walletType: string;
  onSelect: (t: TradeType) => void;
}> = ({ walletType, onSelect }) => {
  const [trades, setTrades] = useState<TradeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });

  const load = (p: number) => {
    setLoading(true);
    tradeService
      .listTrades({
        status: "closed",
        wallet_type: walletType,
        page: p,
        per_page: 15,
      })
      .then((res) => {
        setTrades(res.data.data || []);
        setMeta({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setPage(1);
  }, [walletType]);
  useEffect(() => {
    load(page);
  }, [page, walletType]);

  if (trades.length === 0 && !loading)
    return (
      <div className="flex flex-col items-center justify-center py-14">
        <Ico name="history" className="w-7 h-7 text-gray-800 mb-3" />
        <p className="text-gray-600 text-sm">No trade history yet</p>
      </div>
    );

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-1.5 px-3 py-2 border-b border-gray-800/50">
            {["Symbol", "Size", "Entry", "Close", "P&L", "Date", "By"].map(
              (h) => (
                <span
                  key={h}
                  className="text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                >
                  {h}
                </span>
              ),
            )}
          </div>
          <div className="divide-y divide-gray-800/20">
            {trades.map((t) => {
              const isBuy = t.direction === "buy";
              return (
                <div
                  key={t.id}
                  onClick={() => onSelect(t)}
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-1.5 px-3 py-2.5 items-center hover:bg-gray-900 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-[8px] font-black shrink-0"
                      style={{
                        background: `${COIN_COLORS[t.cryptocurrency]}15`,
                        color: COIN_COLORS[t.cryptocurrency],
                      }}
                    >
                      {t.cryptocurrency[0]}
                    </div>
                    <div>
                      <p className="text-gray-400 text-[11px] font-mono">
                        {t.cryptocurrency}/USDT
                      </p>
                      <span
                        className={`text-[8px] font-bold ${isBuy ? "text-primary/50" : "text-red-500/50"}`}
                      >
                        {isBuy ? "▲ L" : "▼ S"} · {t.leverage}x
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-500 text-[11px] font-mono">
                    {formatUSD(t.amount)}
                  </p>
                  <p className="text-gray-600 text-[11px] font-mono">
                    $
                    {parseFloat(String(t.entry_price)).toLocaleString(
                      undefined,
                      { maximumFractionDigits: 2 },
                    )}
                  </p>
                  <p className="text-gray-600 text-[11px] font-mono">
                    $
                    {parseFloat(String(t.close_price || 0)).toLocaleString(
                      undefined,
                      { maximumFractionDigits: 2 },
                    )}
                  </p>
                  <p
                    className={`text-[11px] font-mono font-bold ${pnlClass(t.profit_loss)}`}
                  >
                    {formatPnL(t.profit_loss)}
                  </p>
                  <p className="text-gray-400 text-[10px] font-mono">
                    {(t.opened_at || t.created_at)?.slice(0, 10) || "—"}
                  </p>
                  <CloseReason reason={(t as any).close_reason} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Pagination
        currentPage={meta.current_page}
        lastPage={meta.last_page}
        total={meta.total}
        from={meta.from}
        to={meta.to}
        onPageChange={(p) => setPage(p)}
        loading={loading}
      />
    </div>
  );
};

// ── Order Form ────────────────────────────────────────────────────────────────
const OrderForm: React.FC<{
  register: any;
  handleSubmit: any;
  watch: any;
  setValue: any;
  errors: any;
  walletType: "real" | "demo";
  currentBalance: number;
  currentPrice: number;
  loading: boolean;
  showSlTp: boolean;
  setShowSlTp: (v: boolean) => void;
  onSwitchWallet: (t: "real" | "demo") => void;
  onBuy: () => void;
  onSell: () => void;
}> = ({
  register,
  handleSubmit,
  watch,
  setValue,
  errors,
  walletType,
  currentBalance,
  currentPrice,
  loading,
  showSlTp,
  setShowSlTp,
  onSwitchWallet,
  onBuy,
  onSell,
}) => {
  const [side, setSide] = useState<"buy" | "sell">("buy");
  const leverage = watch("leverage");
  const amount = watch("amount");
  const slVal = watch("stop_loss");
  const tpVal = watch("take_profit");
  const estimatedPnl =
    amount && Number(amount) > 0 && currentPrice > 0
      ? (Number(amount) * 0.01 * Number(leverage)).toFixed(2)
      : null;

  return (
    <div className="flex flex-col h-full">
      {/* Account picker */}
      <div className="p-3 border-b border-gray-800/40">
        <div className="flex bg-gray-900/80 rounded-xl p-0.5 gap-0.5 border border-gray-800/60">
          {(["real", "demo"] as const).map((t) => (
            <button
              key={t}
              onClick={() => onSwitchWallet(t)}
              className={`flex-1 py-2 rounded-[10px] text-xs font-display font-bold transition-all flex items-center justify-center gap-1.5 ${
                walletType === t
                  ? t === "real"
                    ? "bg-primary text-[var(--on-primary)]"
                    : "bg-blue-500 text-gray-50"
                  : "text-gray-600 hover:text-gray-400"
              }`}
            >
              {t === "real" ? "Real" : "Demo"}
            </button>
          ))}
        </div>
        <div className="flex justify-between items-center mt-2 px-0.5">
          <span className="text-gray-600 text-[10px]">Available balance</span>
          <span
            className={`text-xs font-mono font-bold ${walletType === "real" ? "text-primary" : "text-blue-400"}`}
          >
            {formatUSD(currentBalance || 0)}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1">
        {/* Buy / Sell tabs (XT style) */}
        <div className="grid grid-cols-2 bg-gray-900 rounded-xl p-1 gap-1">
          <button
            type="button"
            onClick={() => setSide("buy")}
            className={`py-2 rounded-lg text-[12px] font-display font-black tracking-wider transition-all ${
              side === "buy"
                ? "bg-primary text-[var(--on-primary)]"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            Buy
          </button>
          <button
            type="button"
            onClick={() => setSide("sell")}
            className={`py-2 rounded-lg text-[12px] font-display font-black tracking-wider transition-all ${
              side === "sell"
                ? "bg-[#E75560] text-white"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            Sell
          </button>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
            Trade Amount (USD)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm pointer-events-none">
              $
            </span>
            <input
              type="number"
              placeholder="0.00"
              className="w-full bg-gray-900/60 border border-gray-800 focus:border-gray-600 rounded-xl pl-7 pr-3 py-2.5 text-sm text-gray-50 font-mono placeholder-gray-800 outline-none transition-colors"
              {...register("amount")}
            />
          </div>
          {errors.amount && (
            <p className="text-red-500 text-[10px] mt-1 px-1">
              {errors.amount.message}
            </p>
          )}
        </div>

        {/* Leverage */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Leverage
            </label>
            <span className="text-yellow-400 text-xs font-mono font-bold">
              {leverage}×
            </span>
          </div>
          <div className="flex gap-1 flex-wrap">
            {(LEVERAGE_OPTIONS as number[]).map((lev: number) => (
              <button
                key={lev}
                type="button"
                onClick={() => setValue("leverage", String(lev))}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold border transition-all ${
                  watch("leverage") === String(lev)
                    ? "bg-yellow-500/20 border-yellow-500/30 text-yellow-400"
                    : "bg-gray-900/40 border-gray-800 text-gray-600 hover:text-gray-300 hover:border-gray-700"
                }`}
              >
                {lev}×
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
            Auto-Close Duration
          </label>
          <select
            className="w-full bg-gray-900/60 border border-gray-800 focus:border-gray-600 rounded-xl px-3 py-2 text-xs text-gray-300 outline-none transition-colors"
            {...register("duration_minutes")}
          >
            <option value="0">No auto-close</option>
            {(DURATION_OPTIONS as any[]).map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        {/* SL / TP */}
        <button
          type="button"
          onClick={() => setShowSlTp(!showSlTp)}
          className="flex items-center justify-between py-2 px-3 bg-gray-900/40 hover:bg-gray-900/70 rounded-xl border border-gray-800 hover:border-gray-700 transition-all"
        >
          <span className="text-xs text-gray-500 font-semibold">
            Stop Loss / Take Profit
          </span>
          <span className="flex items-center gap-1.5">
            {(slVal || tpVal) && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            )}
            <Ico
              name={showSlTp ? "arrow_up" : "arrow_down"}
              className="w-3 h-3 text-gray-700"
            />
          </span>
        </button>

        {showSlTp && (
          <div className="flex flex-col gap-2.5 p-3 bg-gray-900 border border-gray-700 rounded-xl">
            <p className="text-[9px] text-gray-700">
              Auto-closed by server every minute.
            </p>
            <div>
              <label className="block text-[9px] font-bold text-red-500/70 uppercase tracking-wide mb-1">
                🛑 Stop Loss
              </label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-700 text-xs pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  step="any"
                  placeholder={
                    currentPrice > 0
                      ? `e.g. ${(currentPrice * 0.95).toFixed(0)}`
                      : "Price level"
                  }
                  className="w-full bg-gray-900 border border-red-900/40 focus:border-red-500/40 rounded-lg pl-5 pr-2.5 py-1.5 text-xs text-gray-50 placeholder-gray-800 outline-none transition-colors font-mono"
                  {...register("stop_loss")}
                />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-bold text-primary/70 uppercase tracking-wide mb-1">
                ✅ Take Profit
              </label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-700 text-xs pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  step="any"
                  placeholder={
                    currentPrice > 0
                      ? `e.g. ${(currentPrice * 1.05).toFixed(0)}`
                      : "Price level"
                  }
                  className="w-full bg-gray-900 border border-primary/15 focus:border-primary/40 rounded-lg pl-5 pr-2.5 py-1.5 text-xs text-gray-50 placeholder-gray-800 outline-none transition-colors font-mono"
                  {...register("take_profit")}
                />
              </div>
            </div>
          </div>
        )}

        {/* Auto-compound */}
        <div className="flex items-center justify-between py-2 px-3 bg-gray-900/30 rounded-xl border border-gray-800/50">
          <span className="text-[11px] text-gray-500 font-semibold">
            Auto-Compound
          </span>
          <input
            type="checkbox"
            className="w-4 h-4 accent-primary"
            {...register("auto_compound")}
          />
        </div>

        {/* PnL estimate */}
        {estimatedPnl && (
          <div className="flex items-center justify-between px-3 py-2 bg-primary/5 border border-primary/10 rounded-xl">
            <span className="text-[10px] text-gray-600">
              Est. on 1% move ({leverage}×)
            </span>
            <span className="text-primary text-xs font-mono font-bold">
              +${estimatedPnl}
            </span>
          </div>
        )}

        {/* Action button follows selected side */}
        <button
          type="button"
          disabled={loading}
          onClick={side === "buy" ? onBuy : onSell}
          className={`relative w-full py-3.5 rounded-xl font-display font-black text-sm tracking-widest transition-all disabled:opacity-40 overflow-hidden ${
            side === "buy"
              ? "bg-primary hover:bg-primary-dark text-[var(--on-primary)]"
              : "bg-[#E75560] hover:bg-[#D6414C] text-white"
          }`}
        >
          {side === "buy" ? "▲ Buy / Long" : "▼ Sell / Short"}
          {loading && <div className="absolute inset-0 bg-black/10 animate-pulse" />}
        </button>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const Trade: React.FC = () => {
  const dispatch = useAppDispatch();
  const { openTrades, wallets } = useAppSelector((s) => s.trade);
  const selectedWalletMode = useAppSelector((s) => s.ui.selectedWallet);
  const { prices, marketData, connected } = usePrices() as any;

  const [selectedCoin, setSelectedCoin] = useState("BTC");
  const [activeTab, setActiveTab] = useState<"open" | "closed">("open");
  const [loading, setLoading] = useState(false);
  const [closingId, setClosingId] = useState<number | null>(null);
  const [showSlTp, setShowSlTp] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<"chart" | "trade">("chart");
  const [selectedTrade, setSelectedTrade] = useState<TradeType | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      cryptocurrency: "BTC",
      amount: "",
      wallet_type: selectedWalletMode,
      leverage: "1",
      duration_minutes: "0",
      auto_compound: false,
      stop_loss: "",
      take_profit: "",
    },
  });

  useEffect(() => {
    dispatch(fetchOpenTrades());
    dispatch(fetchWallets());
  }, [dispatch]);
  useEffect(() => {
    setValue("wallet_type", selectedWalletMode);
  }, [selectedWalletMode, setValue]);

  const walletType = watch("wallet_type") as "real" | "demo";
  const currentBalance =
    walletType === "demo" ? wallets.demo?.balance : wallets.real?.balance;
  const currentPrice = prices[selectedCoin] || 0;
  const coinData = marketData[selectedCoin];

  const switchWallet = (type: "real" | "demo") => {
    setValue("wallet_type", type);
    dispatch(setSelectedWallet(type));
    localStorage.setItem("accountMode", type);
  };

  const submitTrade = async (direction: "buy" | "sell", data: FormData) => {
    setLoading(true);
    try {
      const payload: any = {
        cryptocurrency: data.cryptocurrency,
        direction,
        amount: Number(data.amount),
        wallet_type: data.wallet_type,
        leverage: Number(data.leverage),
        duration_minutes: Number(data.duration_minutes) || undefined,
        auto_compound: data.auto_compound,
      };
      if (data.stop_loss && Number(data.stop_loss) > 0)
        payload.stop_loss = Number(data.stop_loss);
      if (data.take_profit && Number(data.take_profit) > 0)
        payload.take_profit = Number(data.take_profit);
      await tradeService.openTrade(payload);
      toast.success(
        `${direction === "buy" ? "▲ LONG" : "▼ SHORT"} opened — ${data.cryptocurrency}`,
      );
      setValue("amount", "");
      setValue("stop_loss", "");
      setValue("take_profit", "");
      dispatch(fetchOpenTrades());
      dispatch(fetchWallets());
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to open trade");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async (trade: TradeType) => {
    setClosingId(trade.id);
    try {
      await tradeService.closeTrade(trade.id);
      toast.success("Position closed");
      dispatch(fetchOpenTrades());
      dispatch(fetchWallets());
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setClosingId(null);
    }
  };

  const filteredOpen = openTrades.filter((t) => t.wallet_type === walletType);

  const formProps = {
    register,
    handleSubmit,
    watch,
    setValue,
    errors,
    walletType,
    currentBalance: Number(currentBalance || 0),
    currentPrice,
    loading,
    showSlTp,
    setShowSlTp,
    onSwitchWallet: switchWallet,
    onBuy: () => handleSubmit((d) => submitTrade("buy", d))(),
    onSell: () => handleSubmit((d) => submitTrade("sell", d))(),
  };

  return (
    <div
      className="flex flex-col bg-card"
      style={{ minHeight: "calc(100vh - 48px)" }}
    >
      {/* ── Mobile panel toggle bar ──────────────────────────────────── */}
      <div className="xl:hidden flex border-b border-gray-800/50 bg-card">
        <button
          onClick={() => setMobilePanel("chart")}
          className={`flex-1 py-2.5 text-xs font-display font-bold flex items-center justify-center gap-1.5 border-b-2 transition-all ${
            mobilePanel === "chart"
              ? "border-primary text-gray-50"
              : "border-transparent text-gray-600"
          }`}
        >
          <Ico name="chart" className="w-3.5 h-3.5" /> Chart & Order Book
        </button>
        <button
          onClick={() => setMobilePanel("trade")}
          className={`flex-1 py-2.5 text-xs font-display font-bold flex items-center justify-center gap-1.5 border-b-2 transition-all ${
            mobilePanel === "trade"
              ? "border-primary text-gray-50"
              : "border-transparent text-gray-600"
          }`}
        >
          <Ico name="wallet" className="w-3.5 h-3.5" /> Place Trade
          {Number(currentBalance || 0) > 0 && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </button>
      </div>

      {/* ── Desktop: side by side. Mobile: toggled panels ────────────── */}
      <div
        className="flex flex-col xl:flex-row flex-1 min-h-0"
        style={{ minHeight: 500 }}
      >
        {/* ── Chart column ── */}
        <div
          className={`flex-1 flex flex-col min-w-0 border-b xl:border-b-0 xl:border-r border-gray-800/50 ${mobilePanel === "trade" ? "hidden xl:flex" : "flex"}`}
        >
          {/* Chart header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800/40 bg-card shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shrink-0"
                style={{
                  background: `${COIN_COLORS[selectedCoin]}18`,
                  color: COIN_COLORS[selectedCoin],
                }}
              >
                {selectedCoin[0]}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <PairSelector
                    selectedCoin={selectedCoin}
                    prices={prices}
                    marketData={marketData}
                    onSelect={(s) => {
                      setSelectedCoin(s);
                      setValue("cryptocurrency", s);
                    }}
                  />
                  <span
                    className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      connected !== false
                        ? "bg-primary/10 text-primary"
                        : "bg-gray-800 text-gray-500"
                    }`}
                  >
                    <span
                      className={`w-1 h-1 rounded-full ${connected !== false ? "bg-primary animate-pulse" : "bg-gray-600"}`}
                    />
                    {connected !== false ? "LIVE" : "REST"}
                  </span>
                </div>
                {coinData && (
                  <div className="flex gap-3 flex-wrap">
                    {[
                      {
                        l: "24h",
                        v: `${coinData.change_24h >= 0 ? "+" : ""}${(coinData.change_24h || 0).toFixed(2)}%`,
                        c:
                          coinData.change_24h >= 0
                            ? "text-primary"
                            : "text-red-500",
                      },
                      {
                        l: "Vol",
                        v: coinData.volume_24h
                          ? `$${(coinData.volume_24h / 1e9).toFixed(2)}B`
                          : "—",
                        c: "text-gray-400",
                      },
                    ].map((s) => (
                      <span key={s.l} className="flex items-center gap-1">
                        <span className="text-gray-600 text-[9px]">{s.l}</span>
                        <span
                          className={`text-[10px] font-mono font-semibold ${s.c}`}
                        >
                          {s.v}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <LivePrice
                price={currentPrice}
                className="font-bold text-lg"
                baseColor="text-gray-50"
                showArrow
              />
              {coinData && (
                <p
                  className={`text-[10px] font-mono ${coinData.change_24h >= 0 ? "text-primary" : "text-red-500"}`}
                >
                  {coinData.change_24h >= 0 ? "+" : ""}
                  {coinData.change_24h?.toFixed(2)}% 24h
                </p>
              )}
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 min-h-0 relative">
            <TradingViewChart symbol={selectedCoin} height={500} />
          </div>

          {/* Order Book below chart on mobile only */}
          <div
            className="border-t border-gray-800/50 xl:hidden shrink-0"
            style={{ height: 300 }}
          >
            <OrderBook symbol={selectedCoin} />
          </div>
        </div>

        {/* ── Desktop order book column ─────────────────────────────── */}
        <div className="hidden xl:flex w-[200px] shrink-0 border-r border-gray-800/50 flex-col">
          <OrderBook symbol={selectedCoin} />
        </div>

        {/* ── Order form ── */}
        <div
          className={`w-full xl:w-[290px] shrink-0 bg-card overflow-y-auto ${mobilePanel === "chart" ? "hidden xl:flex xl:flex-col" : "flex flex-col"}`}
        >
          <OrderForm {...formProps} />
        </div>
      </div>

      {/* ── Positions panel ─────────────────────────────────────────── */}
      <div
        className="border-t border-gray-800/60 bg-card"
        style={{ minHeight: 260 }}
      >
        <div className="flex items-center border-b border-gray-800/40 px-2 bg-card">
          {[
            { id: "open", label: "Open Positions", count: filteredOpen.length },
            { id: "closed", label: "History", count: null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2.5 text-xs font-display font-bold border-b-2 transition-all flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? "border-primary text-gray-50"
                  : "border-transparent text-gray-600 hover:text-gray-400"
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className="bg-primary/15 text-primary text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 pr-2">
            <span
              className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${walletType === "real" ? "bg-primary/10 text-primary" : "bg-blue-500/10 text-blue-400"}`}
            >
              {walletType.toUpperCase()}
            </span>
            <button
              onClick={() => {
                dispatch(fetchOpenTrades());
                dispatch(fetchWallets());
              }}
              className="p-1.5 rounded-lg text-gray-700 hover:text-gray-400 hover:bg-gray-800/50 transition-all"
            >
              <Ico name="refresh" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {activeTab === "open" && (
          <OpenPositionsTable
            trades={filteredOpen}
            prices={prices}
            onClose={handleClose}
            onSelect={(t) => setSelectedTrade(t)}
            closingId={closingId}
          />
        )}
        {activeTab === "closed" && (
          <ClosedTradesTable
            walletType={walletType}
            onSelect={(t) => setSelectedTrade(t)}
          />
        )}

        {/* ── Trade Detail Modal ──────────────────────────────────────── */}
        {selectedTrade &&
          (() => {
            const t = selectedTrade;
            const isBuy = t.direction === "buy";
            const isOpen = t.status === "open";
            const entry = parseFloat(String(t.entry_price || 0));
            const closePx = parseFloat(String(t.close_price || 0));
            const markPx = prices[t.cryptocurrency] || 0;
            const amount = parseFloat(String(t.amount || 0));
            const leverage = Number(t.leverage) || 1;
            const rawPnl = isOpen
              ? (t.unrealised_pnl ?? 0)
              : parseFloat(String(t.profit_loss || 0));
            const livePnl = (() => {
              if (!isOpen || entry <= 0 || markPx <= 0) return rawPnl;
              const priceDelta = isBuy ? markPx - entry : entry - markPx;
              return (priceDelta / entry) * amount * leverage;
            })();
            const pnl = isOpen ? livePnl : rawPnl;
            const exitPx = isOpen ? markPx : closePx > 0 ? closePx : 0;
            const diff = entry > 0 && exitPx > 0 ? exitPx - entry : null;
            const movePct =
              diff !== null && entry > 0 ? (diff / entry) * 100 : null;
            const pnlPct = amount > 0 ? (pnl / amount) * 100 : 0;
            const notional = amount * leverage;
            const isProfit = pnl >= 0;
            const isDirectionProfit = isBuy
              ? (diff ?? 0) >= 0
              : (diff ?? 0) <= 0;
            const coinColor = COIN_COLORS[t.cryptocurrency] || "#888";
            const openedTime =
              (t.opened_at || t.created_at)?.slice(0, 16)?.replace("T", " ") ||
              "—";

            return (
              <Modal
                isOpen={true}
                onClose={() => setSelectedTrade(null)}
                title=""
                size="xl"
              >
                <div
                  className="flex flex-col overflow-hidden rounded-2xl"
                  style={{ margin: "-1.25rem", marginTop: "-4.5rem" }}
                >
                  {/* ── Hero banner ── */}
                  <div
                    className={`relative px-5 pt-5 pb-4 shrink-0 ${isProfit ? "bg-gradient-to-br from-emerald-50 dark:from-emerald-950 via-[var(--tint-up-a)] to-[var(--tint-up-b)]" : "bg-gradient-to-br from-red-50 dark:from-red-950 via-[var(--tint-down-a)] to-[var(--tint-down-b)]"}`}
                  >
                    <button
                      onClick={() => setSelectedTrade(null)}
                      className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-50/50 hover:text-gray-50 transition-all text-xs z-10"
                    >
                      ✕
                    </button>
                    <div className="flex max-sm:flex-col items-start gap-3 pr-8">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-black shrink-0 shadow-lg"
                        style={{
                          background: `${coinColor}30`,
                          color: coinColor,
                          border: `2px solid ${coinColor}40`,
                        }}
                      >
                        {t.cryptocurrency[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-50 font-mono font-black text-lg leading-tight">
                          {t.cryptocurrency}/USDT
                        </p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span
                            className={`text-[9px] font-black px-2 py-0.5 rounded-lg ${isBuy ? "bg-emerald-500/30 text-emerald-300 border border-emerald-500/40" : "bg-red-500/30 text-red-300 border border-red-500/40"}`}
                          >
                            {isBuy ? "▲ LONG" : "▼ SHORT"}
                          </span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-lg bg-yellow-500/20 text-yellow-600 border border-yellow-500/30">
                            {leverage}×
                          </span>
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-lg ${t.wallet_type === "real" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-blue-500/20 text-blue-600 border border-blue-500/30"}`}
                          >
                            {t.wallet_type?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-right max-sm:text-left shrink-0">
                        <p
                          className={`text-2xl font-mono font-black leading-tight ${isProfit ? "text-emerald-300" : "text-red-500"}`}
                        >
                          {formatPnL(pnl)}
                        </p>
                        <p
                          className={`text-sm font-mono font-bold mt-0.5 ${isProfit ? "text-emerald-400" : "text-red-500"}`}
                        >
                          {pnlPct >= 0 ? "+" : ""}
                          {pnlPct.toFixed(2)}% ROI
                        </p>
                        <p className="text-gray-50/30 text-[10px] mt-0.5 font-mono">
                          #{t.id}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Body: flex-col on mobile, flex-row on xl ── */}
                  <div
                    className="flex flex-col xl:flex-row bg-panel overflow-y-auto"
                    style={{ maxHeight: "65vh" }}
                  >
                    {/* Analysis */}
                    <div className="flex-1 p-4 flex flex-col gap-3 min-w-0">
                      {/* Price stats */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          {
                            l: "Size",
                            v: formatUSD(amount),
                            sub: `${formatUSD(notional)} notional`,
                            vc: "text-gray-50",
                            sc: "text-gray-400",
                          },
                          {
                            l: "Entry",
                            v:
                              entry > 0
                                ? `$${entry.toLocaleString(undefined, { maximumFractionDigits: entry < 1 ? 5 : 2 })}`
                                : "—",
                            sub: openedTime,
                            vc: "text-gray-100",
                            sc: "text-gray-500",
                          },
                          {
                            l: isOpen ? "Mark" : "Exit",
                            v:
                              exitPx > 0
                                ? `$${exitPx.toLocaleString(undefined, { maximumFractionDigits: exitPx < 1 ? 5 : 2 })}`
                                : "—",
                            sub:
                              movePct !== null
                                ? `${movePct >= 0 ? "+" : ""}${movePct.toFixed(3)}%`
                                : "",
                            vc: isOpen ? "text-emerald-300" : "text-gray-100",
                            sc:
                              movePct !== null
                                ? movePct >= 0
                                  ? "text-emerald-400"
                                  : "text-red-500"
                                : "text-gray-500",
                          },
                        ].map((item) => (
                          <div
                            key={item.l}
                            className="p-2.5 bg-panel border border-gray-700 rounded-xl"
                          >
                            <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">
                              {item.l}
                            </p>
                            {item.l === (isOpen ? "Mark" : "Exit") &&
                            isOpen &&
                            markPx > 0 ? (
                              <LivePrice
                                price={markPx}
                                className={`text-sm font-bold ${item.vc}`}
                                baseColor={item.vc}
                                showArrow
                              />
                            ) : (
                              <p
                                className={`text-sm font-mono font-bold ${item.vc} truncate`}
                              >
                                {item.v}
                              </p>
                            )}
                            {item.sub && (
                              <p
                                className={`text-[9px] font-mono mt-0.5 truncate ${item.sc}`}
                              >
                                {item.sub}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Price movement bar */}
                      {diff !== null && entry > 0 && exitPx > 0 && (
                        <div className="p-3 bg-panel border border-gray-700 rounded-xl">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                              Price Movement
                            </span>
                            <span
                              className={`text-[9px] font-bold px-2 py-0.5 rounded-lg ${isDirectionProfit ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-500 border border-red-500/30"}`}
                            >
                              {isDirectionProfit ? "✓ Profitable" : "✗ Against"}
                            </span>
                          </div>
                          <div className="relative h-4 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className={`absolute inset-y-0 left-0 rounded-full transition-all ${isDirectionProfit ? "bg-gradient-to-r from-emerald-600 to-emerald-400" : "bg-gradient-to-r from-red-700 to-red-500"}`}
                              style={{
                                width: `${Math.min(Math.abs(movePct ?? 0) * 10, 96)}%`,
                                opacity: 0.9,
                              }}
                            />
                          </div>
                          <div className="flex justify-between text-[9px] font-mono mt-1.5 gap-1">
                            <span className="text-gray-500 truncate">
                              $
                              {entry.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}
                            </span>
                            <span
                              className={`font-bold ${isDirectionProfit ? "text-emerald-300" : "text-red-500"}`}
                            >
                              {diff >= 0 ? "+" : ""}$
                              {Math.abs(diff).toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}{" "}
                              ({movePct?.toFixed(2)}%)
                            </span>
                            <span className="text-gray-500 truncate">
                              $
                              {exitPx.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* SL / TP */}
                      {(t.stop_loss || t.take_profit) && (
                        <div className="grid grid-cols-2 gap-2">
                          {t.stop_loss ? (
                            <div className="p-2.5 bg-red-950/40 border border-red-500/30 rounded-xl">
                              <p className="text-[9px] text-red-500 uppercase tracking-wide font-bold mb-1">
                                🛑 Stop Loss
                              </p>
                              <p className="text-red-300 font-mono font-black text-base">
                                $
                                {parseFloat(String(t.stop_loss)).toLocaleString(
                                  undefined,
                                  { maximumFractionDigits: 4 },
                                )}
                              </p>
                              {entry > 0 && (
                                <p className="text-red-500/70 text-[9px] font-mono mt-0.5">
                                  {(
                                    (Math.abs(
                                      parseFloat(String(t.stop_loss)) - entry,
                                    ) /
                                      entry) *
                                    100
                                  ).toFixed(2)}
                                  % from entry
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="p-2.5 bg-panel border border-gray-700/60 rounded-xl flex items-center justify-center">
                              <p className="text-gray-600 text-xs">No SL</p>
                            </div>
                          )}
                          {t.take_profit ? (
                            <div className="p-2.5 bg-emerald-950/40 border border-emerald-500/30 rounded-xl">
                              <p className="text-[9px] text-emerald-400 uppercase tracking-wide font-bold mb-1">
                                ✅ Take Profit
                              </p>
                              <p className="text-emerald-300 font-mono font-black text-base">
                                $
                                {parseFloat(
                                  String(t.take_profit),
                                ).toLocaleString(undefined, {
                                  maximumFractionDigits: 4,
                                })}
                              </p>
                              {entry > 0 && (
                                <p className="text-emerald-600/70 text-[9px] font-mono mt-0.5">
                                  {(
                                    (Math.abs(
                                      parseFloat(String(t.take_profit)) - entry,
                                    ) /
                                      entry) *
                                    100
                                  ).toFixed(2)}
                                  % from entry
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="p-2.5 bg-panel border border-gray-700/60 rounded-xl flex items-center justify-center">
                              <p className="text-gray-600 text-xs">No TP</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Meta row */}
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          {
                            l: "Status",
                            v: t.status,
                            c: isOpen ? "text-emerald-300" : "text-gray-300",
                          },
                          {
                            l: "Closed By",
                            v:
                              (t as any).close_reason ||
                              (isOpen ? "Open" : "Manual"),
                            c: "text-gray-300",
                          },
                          { l: "Opened", v: openedTime, c: "text-gray-300" },
                          {
                            l: "Closed At",
                            v:
                              (t as any).closed_at
                                ?.slice(0, 16)
                                ?.replace("T", " ") || "—",
                            c: "text-gray-300",
                          },
                        ].map((item) => (
                          <div
                            key={item.l}
                            className="p-2.5 bg-panel border border-gray-700/60 rounded-lg"
                          >
                            <p className="text-[9px] text-gray-600 uppercase tracking-wide">
                              {item.l}
                            </p>
                            <p
                              className={`text-xs font-mono font-semibold mt-0.5 capitalize ${item.c}`}
                            >
                              {item.v}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Action buttons */}
                      {isOpen && (
                        <button
                          onClick={() => {
                            setSelectedTrade(null);
                            handleClose(t);
                          }}
                          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-500 hover:bg-red-500/25 hover:border-red-500/50 transition-all font-bold text-sm"
                        >
                          <Ico name="close" className="w-4 h-4" /> Close This
                          Position
                        </button>
                      )}

                      <button
                        onClick={() => setSelectedTrade(null)}
                        className="py-2.5 rounded-xl bg-gray-900 border border-gray-700 text-gray-400 hover:text-gray-50 hover:bg-gray-800 transition-all text-xs font-semibold"
                      >
                        Dismiss
                      </button>
                    </div>

                    {/* Order Book — full width on mobile, sidebar on xl */}
                    <div className="xl:w-[195px] xl:shrink-0 border-t xl:border-t-0 xl:border-l border-white/[0.06]">
                      <div className="px-3 pt-2.5 pb-1 border-b border-white/[0.06]">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                          {t.cryptocurrency}/USDT · Order Book
                        </p>
                      </div>
                      {/* Fixed height on mobile so it doesn't go forever */}
                      <div className="h-[260px] xl:h-[400px] overflow-y-auto">
                        <OrderBook symbol={t.cryptocurrency} />
                      </div>
                    </div>
                  </div>
                </div>
              </Modal>
            );
          })()}
      </div>
    </div>
  );
};

export default Trade;
